import { useConnectAndEnableCastAiScriptQuery } from 'hooks/queries/cluster/useConnectAndEnableCastAiScriptQuery';

import {
  ClusterDialogHeading,
  FeaturesScriptSplitDialogContent,
  IRanTheScriptButton,
  ScriptSection,
  StepContainer,
} from '../../_components/new-onboarding';
import { SecurityFeatureSelection } from '../types';
import { SecurityComplianceSetup } from './SecurityComplianceSetup';
import { SecurityVulnerabilityDetectionSetup } from './SecurityVulnerabilityDetectionSetup';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { DialogFooter, Stack } from '@cast/design-system';

type Props = {
  featureSelection: SecurityFeatureSelection;
  updateFeature: (
    feature: keyof SecurityFeatureSelection,
    checked: boolean
  ) => void;
  onNext: () => void;
  onClose: () => void;
};

export const SecurityFeaturesSetupSlide = ({
  featureSelection,
  updateFeature,
  onNext,
  onClose,
}: Props) => {
  const { cluster } = useCluster();
  const { isLoading, isFetching, error, refetch, script } =
    useConnectAndEnableCastAiScriptQuery({
      clusterName: cluster.name,
      clusterProvider: cluster.providerType,
      installSecurityAgent: true,
      installSecurityImageScanning: featureSelection.vulnerabilityDetection,
      installSecurityCompliance: featureSelection.compliance,
      installGPUPlugin: false,
      installAutoscalerAgent: false,
      installNetflowExporter: false,
      installGpuMetricsExporter: false,
      installReliabilityMetrics: false,
      installWorkloadAutoscaler: false,
      installOmni: false,
      installAiOptimizerProxy: false,
      installPodMutator: false,
    });

  const noneSelected =
    !featureSelection.vulnerabilityDetection && !featureSelection.compliance;

  return (
    <StepContainer data-testid="setup-step">
      <ClusterDialogHeading cluster={cluster} onClose={onClose} />

      <FeaturesScriptSplitDialogContent
        leftTitle="Features to be enabled"
        left={
          <Stack>
            <SecurityVulnerabilityDetectionSetup
              checked={featureSelection.vulnerabilityDetection}
              onChange={(checked) =>
                updateFeature('vulnerabilityDetection', checked)
              }
            />
            <SecurityComplianceSetup
              checked={featureSelection.compliance}
              onChange={(checked) => updateFeature('compliance', checked)}
            />
          </Stack>
        }
        rightTitle="Run the script"
        right={
          <ScriptSection
            script={script}
            isLoading={isLoading || isFetching}
            refetch={refetch}
            error={error}
          />
        }
      />

      <DialogFooter
        end={<IRanTheScriptButton onClick={onNext} disabled={noneSelected} />}
      />
    </StepContainer>
  );
};
